import {
  CardTableDataCol,
  CardTableRow,
  Checkbox,
  Chip,
  Text,
  Typography,
  User,
  VariantType,
  createStyles,
  makeStyles,
  useRouter,
  useSnackbar,
} from '@forgeglobal/design-framework'
import {
  GetPricedIssuersDocument,
  IssuerMarketplaceSettingsFormInput,
  PricedIssuerFragment,
  useUpdateIssuerMarketplaceSettingsMutation,
} from 'graphql/bindings/AdminBindings'
import { MoneyFormattedInput, PercentFormattedInput } from 'components/shared/NumericInput'
import { Routes } from 'util/Routes'
import { parseNumber } from 'util/NumberUtils'
import React, { useState } from 'react'
import isEqual from 'lodash/isEqual'
import numeral from 'numeral'

interface PricedIssuerRowProps {
  issuer: PricedIssuerFragment
  limit: number
  offset: number
}

type FormShape = Pick<
  IssuerMarketplaceSettingsFormInput,
  | 'default_share_price_investor'
  | 'default_share_price_shareholder'
  | 'show_pricing_data'
  | 'share_price_variance'
  | 'featured'
  | 'is_available'
  | 'weight'
  | 'min_transaction_size_investor'
>

type BlurredFieldName =
  | 'default_share_price_shareholder'
  | 'default_share_price_investor'
  | 'share_price_variance'
  | 'weight'
  | 'min_transaction_size_investor'

type IssuerCheckboxField = 'is_available' | 'featured' | 'show_pricing_data'

const useStyles = makeStyles(({ spacing }) =>
  createStyles({
    ownersContainer: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    textInput: {
      maxWidth: 150,
    },
    logoContainer: {
      maxWidth: 32,
      maxHeight: 32,
      marginRight: spacing(4),
      marginLeft: spacing(2),
    },
    nameAndLogoContainer: {
      maxWidth: 250,
      display: 'flex',
      alignItems: 'center',
    },
    checkbox_container: {
      display: 'flex',
      justifyContent: 'center',
    },
  })
)

export const PricedIssuerRow: React.FC<PricedIssuerRowProps> = ({ issuer, limit, offset }) => {
  const { history } = useRouter()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()

  const [mutate] = useUpdateIssuerMarketplaceSettingsMutation({
    refetchQueries: [
      {
        query: GetPricedIssuersDocument,
        variables: {
          limit,
          offset,
        },
      },
    ],
  })

  const { id, name, owners, issuer_account } = issuer
  const {
    show_pricing_data,
    default_share_price_investor,
    default_share_price_shareholder,
    share_price_variance,
    featured,
    is_available,
    logo_url,
    weight,
    min_transaction_size_investor,
  } = issuer_account?.company!

  const initialValues = {
    show_pricing_data,
    default_share_price_investor,
    default_share_price_shareholder,
    share_price_variance,
    featured,
    is_available,
    weight,
    min_transaction_size_investor,
  }

  const [formValues, updateValues] = useState(initialValues)

  const validateForm = (currentForm: FormShape): string | undefined => {
    const {
      default_share_price_shareholder,
      default_share_price_investor,
      share_price_variance,
      weight,
      min_transaction_size_investor,
    } = currentForm
    if (default_share_price_shareholder >= default_share_price_investor) {
      return 'ERROR: Implied bid must be lower than implied ask'
    }

    if (default_share_price_shareholder <= 0 || default_share_price_investor <= 0) {
      return 'ERROR: Prices must be greater than zero'
    }

    if (share_price_variance && (share_price_variance <= 0 || share_price_variance > 1)) {
      return 'ERROR: Variance must be greater than zero and less than 100'
    }

    if (weight && (weight <= 0 || weight > 100)) {
      return 'ERROR: Weight must be greater than zero and less than or equal to 100'
    }

    if (min_transaction_size_investor < 0) {
      return 'ERROR: Minimum transaction size must be greater or equal to zero'
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const fieldToUpdate = e.target.name

    const newValue = numeral(e.target.value).value()
    const payload = { ...formValues, [fieldToUpdate]: newValue }

    const errorMessage = validateForm(payload)

    if (errorMessage) {
      enqueueSnackbar(errorMessage, {
        variant: 'error',
      })
      updateValues({
        ...initialValues,
      })
      return
    }

    const convertFieldNameToHumanReadable = (field: BlurredFieldName): string => {
      if (field === 'default_share_price_shareholder') {
        return 'trading range (low)'
      }
      if (field === 'default_share_price_investor') {
        return 'trading range (high)'
      }
      if (field === 'weight') {
        return 'weight'
      }
      if (field === 'min_transaction_size_investor') {
        return 'minimum transaction size'
      }
      return 'variance'
    }

    const snackbarMessage = `${name} ${convertFieldNameToHumanReadable(fieldToUpdate as BlurredFieldName)} updated`
    const snackbarVariant = 'success'

    if (!isEqual(initialValues, payload)) {
      handleSubmit(payload, snackbarMessage, snackbarVariant)
    }
  }

  const handleSubmit = (newValues: FormShape, snackbarMessage: string, snackbarVariant: VariantType | undefined) => {
    const {
      featured,
      is_available,
      show_pricing_data,
      default_share_price_investor,
      default_share_price_shareholder,
      share_price_variance,
      weight,
      min_transaction_size_investor,
    } = newValues
    const variables = {
      form: {
        id,
        owners: owners.map(owner => owner.owner_id),
        show_pricing_data,
        default_share_price_investor,
        default_share_price_shareholder,
        share_price_variance,
        featured,
        is_available,
        weight,
        min_transaction_size_investor,
      },
    }
    mutate({ variables })
      .then(() => {
        enqueueSnackbar(snackbarMessage, {
          variant: snackbarVariant,
        })
      })
      .catch(e => {
        enqueueSnackbar(`ERROR: ${e}`, {
          variant: 'error',
        })
      })
  }

  const handleChange = (value: number, name: string) => {
    updateValues({ ...formValues, [name]: value })
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, checked: boolean) => {
    const field = e.target.name
    const variables = {
      ...initialValues,
      [field]: checked,
    }

    const snackbarVariant = checked ? 'success' : undefined

    const converCheckboxNamesToHumanReadable = (field: IssuerCheckboxField) => {
      if (field === 'featured') {
        return 'Featuring'
      }
      if (field === 'show_pricing_data') {
        return 'Showing pricing for'
      }
      if (field === 'is_available') {
        return 'Is Available flag on'
      }
      return 'Field on'
    }

    const snackbarMessage = `${converCheckboxNamesToHumanReadable(field as IssuerCheckboxField)} ${name} on platform ${
      checked ? 'enabled' : 'disabled'
    }`

    handleSubmit(variables, snackbarMessage, snackbarVariant)
  }

  const checkbox = (fieldName: IssuerCheckboxField, fieldValue: boolean) => (
    <div className={classes.checkbox_container}>
      <Checkbox
        checked={fieldValue}
        onChange={handleCheckboxChange}
        name={fieldName}
        inputProps={{
          'aria-label': `${fieldName} primary checkbox`,
        }}
      />
    </div>
  )

  const columns = {
    issuer: (
      <CardTableDataCol name="issuer">
        <div className={classes.nameAndLogoContainer}>
          <div className={classes.logoContainer}>
            <User alt={name} src={logo_url} />
          </div>
          <Typography variant="subtitle2" onClick={() => history.push(Routes.account(id.toString()))}>
            {name}
          </Typography>
        </div>
      </CardTableDataCol>
    ),
    owners: (
      <div className={classes.ownersContainer}>
        {owners.map((owner: any) => (
          <Chip color="secondary" label={owner.name} key={owner.id} />
        ))}
        {owners.length === 0 && <Chip label="Unowned" />}
      </div>
    ),
    show_pricing_data: checkbox('show_pricing_data', show_pricing_data),
    featured: checkbox('featured', featured),
    is_available: checkbox('is_available', is_available),
    default_share_price_shareholder: (
      <Text
        className={classes.textInput}
        value={formValues.default_share_price_shareholder}
        name="default_share_price_shareholder"
        onChange={e => handleChange(Number(e.target.value), 'default_share_price_shareholder')}
        InputProps={{
          inputComponent: MoneyFormattedInput as any,
        }}
        onBlur={handleBlur}
      />
    ),
    default_share_price_investor: (
      <Text
        className={classes.textInput}
        value={formValues.default_share_price_investor}
        name="default_share_price_investor"
        InputProps={{
          inputComponent: MoneyFormattedInput as any,
        }}
        onChange={e => handleChange(Number(e.target.value), 'default_share_price_investor')}
        onBlur={handleBlur}
      />
    ),
    share_price_variance: (
      <Text
        className={classes.textInput}
        value={formValues.share_price_variance}
        name="share_price_variance"
        InputProps={{
          inputComponent: PercentFormattedInput as any,
        }}
        onChange={e => handleChange(Number(e.target.value), 'share_price_variance')}
        onBlur={handleBlur}
      />
    ),
    weight: (
      <Text
        value={formValues.weight}
        name="weight"
        onChange={e => handleChange(Number(e.target.value), 'weight')}
        onBlur={handleBlur}
        className={classes.textInput}
      />
    ),
    min_transaction_size: (
      <Text
        className={classes.textInput}
        value={numeral(formValues.min_transaction_size_investor)
          .format('($0,0)')
          .toUpperCase()}
        name="min_transaction_size_investor"
        onChange={e => handleChange(parseNumber(e.target.value), 'min_transaction_size_investor')}
        onBlur={handleBlur}
      />
    ),
  }

  return <CardTableRow id={id} columns={columns} />
}
